import { canSee, forRole, helpRoleName, helpViewFor, type ConsoleFaq, type ConsoleIndexEntry, type Role, type ViewId } from '@aithora/shared';

/**
 * The Help page's values (design hdVals, L33605): the index on the left, filtered as you
 * type, and the whole entry (or FAQ answer) on the right. Pure, so it is tested alone.
 * The help text comes from the content loaders; the role filter and the page a title
 * opens come from the shared help functions (packages/shared/src/help.ts).
 */
export type HelpTab = 'guide' | 'faq';

export interface HdItem {
  title: string;
  tag: string | null;
  on: boolean;
}
export interface HdGroup {
  label: string;
  items: HdItem[];
}
export interface HdRow {
  k: string;
  v: string;
  /** kv: a named row; li: a numbered or "!" row. */
  kind: 'kv' | 'li';
  /** kv only: the name is a badge (LIVE, MODEL / MODELED), drawn blue and spaced. */
  mono?: boolean;
}
export interface HdSection {
  label: string;
  rows: HdRow[];
}
export interface HdFaq {
  g: string;
  q: string;
  a: string;
}
export interface HdVals {
  roleName: string;
  count: string;
  listCount: string;
  groups: HdGroup[];
  entry: ConsoleIndexEntry | null;
  meta: string;
  goView: ViewId | null;
  goLabel: string;
  sections: HdSection[];
  see: string[];
  faq: HdFaq | null;
  closing: string;
}

const flat = (a: readonly (string | readonly string[])[] | undefined) => (a ?? []).map((x) => (Array.isArray(x) ? x.join(' ') : x)).join(' ');

export function hdVals(index: readonly ConsoleIndexEntry[], faqData: ConsoleFaq, role: Role | null, s: { tab: HelpTab; q: string; doc: string | null; faq: string | null }): HdVals {
  const roleName = helpRoleName(role);
  const mine = (rs: readonly string[] | undefined) => forRole(rs, roleName);
  const q = s.q.trim().toLowerCase();
  const words = q.split(/\s+/).filter((w) => w.length > 1);
  const match = (txt: string) => !words.length || words.every((w) => txt.toLowerCase().includes(w));
  const ents = index.filter((e) => mine(e.roles)).filter((e) => match(`${e.t} ${e.g} ${e.purpose} ${flat(e.controls)} ${flat(e.gotcha)}`));
  const faqs: HdFaq[] = [];
  faqData.groups.forEach((g) =>
    g.questions.filter((x) => mine(x.roles)).forEach((x) => {
      if (match(`${x.q} ${x.a} ${g.g}`)) faqs.push({ g: g.g, q: x.q, a: x.a });
    }),
  );
  const selT = ents.some((e) => e.t === s.doc) ? s.doc : ents[0] ? ents[0].t : null;
  const selQ = faqs.some((x) => x.q === s.faq) ? s.faq : faqs[0] ? faqs[0].q : null;

  const groups: HdGroup[] = [];
  const groupOf = (label: string) => {
    let g = groups.find((x) => x.label === label);
    if (!g) {
      g = { label, items: [] };
      groups.push(g);
    }
    return g;
  };
  if (s.tab === 'guide') ents.forEach((e) => groupOf(e.g).items.push({ title: e.t, tag: e.state && e.state !== 'LIVE' ? e.state : null, on: e.t === selT }));
  else faqs.forEach((x) => groupOf(x.g).items.push({ title: x.q, tag: null, on: x.q === selQ }));

  const e = s.tab === 'guide' ? index.find((x) => x.t === selT) ?? null : null;
  const sections: HdSection[] = [];
  if (e) {
    if (e.controls && e.controls.length) sections.push({ label: 'What is on the page', rows: e.controls.map((c) => ({ kind: 'kv', k: c[0], v: c[1], mono: /^[A-Z /-]+$/.test(c[0]) })) });
    if (e.truth && e.truth.length) sections.push({ label: 'What is real today', rows: e.truth.map((c) => ({ kind: 'kv', k: c[0], v: c[1], mono: true })) });
    if (e.howTo && e.howTo.length) sections.push({ label: 'How to use it', rows: e.howTo.map((v, i) => ({ kind: 'li', k: String(i + 1), v })) });
    const wrong = [...(e.gotcha ?? []), ...(e.whenNot ?? [])];
    if (wrong.length) sections.push({ label: 'Where people go wrong', rows: wrong.map((v) => ({ kind: 'li', k: '!', v })) });
  }
  // A page the role may not open gets no Go button (the side nav would refuse it too).
  const v = e ? helpViewFor(e.t) : null;
  const goView = v && canSee(role, v) ? v : null;
  const see = e && e.see ? e.see.filter((t) => index.some((x) => x.t === t && mine(x.roles))) : [];
  const fq = s.tab === 'faq' ? faqs.find((x) => x.q === selQ) ?? null : null;
  return {
    roleName,
    count: `${ents.length} pages · ${faqs.length} questions`,
    listCount: s.tab === 'guide' ? `${ents.length}${words.length ? ' match' : ' pages'}` : `${faqs.length}${words.length ? ' match' : ' questions'}`,
    groups,
    entry: e,
    meta: e ? [e.state || '', e.reviewed ? `reviewed ${e.reviewed}` : '', (e.roles || []).join(', ')].filter(Boolean).join(' · ') : '',
    goView,
    goLabel: e ? `Go to ${e.t === 'Scorecard' ? 'Performance' : e.t}` : '',
    sections,
    see,
    faq: fq,
    closing: faqData.closing || '',
  };
}
